(() => {
  const debug = (...args) => {
    try {
      if (window.localStorage?.getItem("mindelorSearchDebug") === "1") {
        console.debug("[mindelor-search]", ...args);
      }
    } catch (error) {
    }
  };

  const getInput = (headerSearch) => headerSearch?.querySelector('input[name="q"]');
  const hasQuery = (headerSearch) => getInput(headerSearch)?.value.trim() !== "";
  const usesDesktopExplicitClose = () => window.matchMedia("screen and (pointer: fine)").matches;
  const closePermissionDuration = 750;

  const keepHeaderVisible = (headerSearch) => {
    const header = headerSearch?.closest("x-header");

    if (!header) {
      return;
    }

    header.classList.remove("is-hidden");
    document.documentElement.style.setProperty("--header-is-visible", "1");
  };

  const ensureOpenVisible = (headerSearch) => {
    if (!headerSearch?.hasAttribute("open")) {
      return;
    }

    headerSearch.style.setProperty("display", "block");
    keepHeaderVisible(headerSearch);
  };

  const hasActiveSearchState = (headerSearch) => {
    const input = getInput(headerSearch);

    return Boolean(
      headerSearch?._mindelorPointerInside ||
      document.activeElement === input ||
      headerSearch?.matches(":focus-within") ||
      input?.value.trim()
    );
  };

  const restoreInputFocus = (headerSearch) => {
    const input = getInput(headerSearch);

    if (!input || input.value.trim() === "" || document.activeElement === input) {
      return;
    }

    requestAnimationFrame(() => {
      if (headerSearch.hasAttribute("open") && input.value.trim() !== "" && document.activeElement !== input) {
        input.focus({ preventScroll: true });
        ensureOpenVisible(headerSearch);
      }
    });
  };

  const hasClosePermission = (headerSearch) => {
    return Boolean(
      headerSearch?._mindelorAllowClose &&
      headerSearch?._mindelorAllowCloseUntil > window.performance.now()
    );
  };

  const clearClosePermission = (headerSearch) => {
    headerSearch._mindelorAllowClose = false;
    headerSearch._mindelorAllowCloseUntil = 0;
    headerSearch._allowNextHide = false;
  };

  const allowNextClose = (headerSearch) => {
    window.clearTimeout(headerSearch._mindelorAllowCloseTimer);
    headerSearch._mindelorAllowClose = true;
    headerSearch._mindelorAllowCloseUntil = window.performance.now() + closePermissionDuration;
    headerSearch._allowNextHide = true;
    headerSearch._mindelorAllowCloseTimer = window.setTimeout(() => {
      if (headerSearch._mindelorAllowCloseUntil <= window.performance.now()) {
        clearClosePermission(headerSearch);
      }
    }, closePermissionDuration);
  };

  const keepPointerInside = (headerSearch) => {
    headerSearch._mindelorPointerInside = true;
  };

  const markPointerOutside = (headerSearch) => {
    if (!usesDesktopExplicitClose()) {
      headerSearch._mindelorPointerInside = false;
    }
  };

  const installVisibilityStyle = () => {
    if (document.getElementById("mindelor-header-search-persistence-style")) {
      return;
    }

    const style = document.createElement("style");
    style.id = "mindelor-header-search-persistence-style";
    style.textContent = `
      .header-search[open] {
        display: block !important;
      }

      .header:has(.header-search[open]) {
        transform: translateY(0);
      }

      .shopify-section--header:has(.header-search[open]) {
        pointer-events: auto;
      }
    `;
    document.head.append(style);
  };

  const setupHeaderSearch = (headerSearch) => {
    if (!headerSearch || headerSearch._mindelorSearchPersistenceReady) {
      return;
    }

    headerSearch._mindelorSearchPersistenceReady = true;
    headerSearch.addEventListener("dialog:before-show", () => ensureOpenVisible(headerSearch));
    headerSearch.addEventListener("dialog:after-show", () => ensureOpenVisible(headerSearch));
    headerSearch.addEventListener("dialog:after-hide", () => {
      if (!headerSearch.hasAttribute("open")) {
        return;
      }

      debug("after-hide while still open", {
        display: headerSearch.style.display,
        headerHidden: headerSearch.closest("x-header")?.classList.contains("is-hidden")
      });
      ensureOpenVisible(headerSearch);
    });
    headerSearch.addEventListener("dialog:force-close", () => allowNextClose(headerSearch), { capture: true });
    headerSearch.addEventListener("focusin", () => ensureOpenVisible(headerSearch));
    headerSearch.addEventListener("input", () => ensureOpenVisible(headerSearch));
    headerSearch.addEventListener("keydown", (event) => {
      if (event.key === "Escape" || event.key === "Esc") {
        allowNextClose(headerSearch);
      }
    }, { capture: true });
    headerSearch.addEventListener("pointerenter", () => {
      keepPointerInside(headerSearch);
      ensureOpenVisible(headerSearch);
    });
    headerSearch.addEventListener("pointerleave", () => {
      markPointerOutside(headerSearch);
    });
    headerSearch.addEventListener("click", (event) => {
      if (event.target.closest?.("a[href]")) {
        allowNextClose(headerSearch);
      }
    }, { capture: true });
    headerSearch.controls?.forEach((control) => {
      control.addEventListener("click", () => {
        if (headerSearch.hasAttribute("open")) {
          allowNextClose(headerSearch);
        }
      }, { capture: true });
      control.addEventListener("pointerenter", () => {
        keepPointerInside(headerSearch);
        ensureOpenVisible(headerSearch);
      });
      control.addEventListener("pointerleave", () => markPointerOutside(headerSearch));
    });
    headerSearch.getShadowPartByName?.("content")?.addEventListener("pointerenter", () => {
      keepPointerInside(headerSearch);
      ensureOpenVisible(headerSearch);
    });
    headerSearch.getShadowPartByName?.("content")?.addEventListener("pointerleave", () => markPointerOutside(headerSearch));
    headerSearch.getShadowPartByName?.("overlay")?.addEventListener("click", () => {
      allowNextClose(headerSearch);
    }, { capture: true });
  };

  const patchHeaderSearch = async () => {
    await window.customElements.whenDefined("header-search");

    const HeaderSearch = window.customElements.get("header-search");
    const originalConnectedCallback = HeaderSearch.prototype.connectedCallback;
    const originalHide = HeaderSearch.prototype.hide;
    const originalHideForOutsideClickTarget = HeaderSearch.prototype.hideForOutsideClickTarget;
    const originalAttributeChangedCallback = HeaderSearch.prototype.attributeChangedCallback;

    HeaderSearch.prototype.connectedCallback = function(...args) {
      originalConnectedCallback?.apply(this, args);
      setupHeaderSearch(this);
    };

    HeaderSearch.prototype.hide = function(...args) {
      if (this.hasAttribute("open") && !hasClosePermission(this) && (usesDesktopExplicitClose() || hasActiveSearchState(this))) {
        debug("blocked non-explicit hide", {
          query: getInput(this)?.value,
          pointerInside: this._mindelorPointerInside,
          focusWithin: this.matches(":focus-within")
        });
        ensureOpenVisible(this);
        return Promise.resolve();
      }

      const isExplicitClose = hasClosePermission(this);
      this._mindelorAllowClose = false;
      this._mindelorExplicitClose = isExplicitClose;

      const hideResult = originalHide.apply(this, args);
      Promise.resolve(hideResult).finally(() => {
        this._mindelorExplicitClose = false;
      });

      return hideResult;
    };

    HeaderSearch.prototype.hideForOutsideClickTarget = function(target) {
      const shouldHide = originalHideForOutsideClickTarget.call(this, target);

      if (shouldHide) {
        allowNextClose(this);
      }

      return shouldHide;
    };

    HeaderSearch.prototype.attributeChangedCallback = function(name, oldValue, newValue) {
      const shouldRestoreOpen = name === "open" &&
        oldValue !== null &&
        newValue === null &&
        !hasClosePermission(this) &&
        !this._mindelorExplicitClose &&
        (usesDesktopExplicitClose() || hasActiveSearchState(this));

      if (shouldRestoreOpen) {
        debug("restore open after non-explicit removal", {
          query: getInput(this)?.value,
          display: this.style.display,
          headerHidden: this.closest("x-header")?.classList.contains("is-hidden")
        });
        originalAttributeChangedCallback.call(this, name, oldValue, newValue);
        queueMicrotask(() => {
          if (!this.hasAttribute("open") && (usesDesktopExplicitClose() || hasActiveSearchState(this))) {
            this.setAttribute("open", "immediate");
            ensureOpenVisible(this);
            restoreInputFocus(this);
          }
        });
        return;
      }

      originalAttributeChangedCallback.call(this, name, oldValue, newValue);

      if (name === "open" && newValue !== null) {
        ensureOpenVisible(this);
      } else if (name === "open" && newValue === null) {
        clearClosePermission(this);
      }
    };

    document.querySelectorAll("header-search").forEach(setupHeaderSearch);
  };

  const patchPredictiveSearch = async () => {
    await window.customElements.whenDefined("predictive-search");

    document.querySelectorAll("predictive-search").forEach((predictiveSearch) => {
      if (predictiveSearch._mindelorSearchPersistenceReady) {
        return;
      }

      const resultsSlot = predictiveSearch.querySelector('[slot="results"]');

      if (!resultsSlot) {
        return;
      }

      predictiveSearch._mindelorSearchPersistenceReady = true;
      new MutationObserver(() => {
        const headerSearch = predictiveSearch.closest("header-search");

        if (!headerSearch?.hasAttribute("open") || !hasQuery(headerSearch)) {
          return;
        }

        debug("predictive results replaced", {
          display: headerSearch.style.display,
          headerHidden: headerSearch.closest("x-header")?.classList.contains("is-hidden")
        });
        ensureOpenVisible(headerSearch);
        restoreInputFocus(headerSearch);
      }).observe(resultsSlot, { childList: true });
    });
  };

  installVisibilityStyle();
  patchHeaderSearch().catch((error) => debug("header-search patch failed", error));
  patchPredictiveSearch().catch((error) => debug("predictive-search patch failed", error));
})();
